"use client";
import React from "react";
import { Box, Typography, Button, Container } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Image from "next/image";
import Link from "next/link";
import { IMAGES } from "../constants/images";
import { useTranslation } from "react-i18next";

const OnfolkTestimonial = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  return (
    <Box
      sx={{
        backgroundImage: `url('${IMAGES.TESTIMONIAL_BG}')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        py: { xs: 6, sm: 8, md: 12 },
        direction: isArabic ? "rtl" : "ltr",
      }}
    >
      <Container
        maxWidth="xl"
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "center",
          justifyContent: "space-between",
          gap: { xs: 4, md: 6 },
        }}
      >
        {/* LEFT SIDE */}
        <Box
          sx={{
            flex: 1,
            maxWidth: { xs: "100%", md: 600 },
            textAlign: isArabic ? "right" : "left",
            px: { xs: 3, sm: 4, md: 0 },
            mb: { xs: 6, md: 0 },
          }}
        >
          {/* Quote */}
          <Typography
            sx={{
              fontSize: {
                xs: "1.1rem",
                sm: "1.25rem",
                md: "1.5rem",
                lg: "1.5rem",
                xl: "1.9rem",
              },
              lineHeight: 1.6,
              color: "#121212",
              fontWeight: 400,
              mb: 4,
              fontFamily: "var(--font-plusjakarta)",
            }}
          >
            {t("onfolkTestimonial.quote")}
          </Typography>

          {/* Stats Boxes */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 3, sm: 4 },
              mb: 4,
              backgroundColor: "#F3DDFF",
              borderRadius: "16px",
              p: 3,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  color: "#9048FB",
                  fontWeight: 600,
                  fontSize: { xs: "1.25rem", md: "1.5rem" },
                  mb: 0.5,
                  fontFamily: "var(--font-plusjakarta)",
                }}
              >
                {t("onfolkTestimonial.stat1Label")}
              </Typography>
              <Typography
                sx={{
                  color: "#121212",
                  fontSize: { xs: "0.875rem", md: "1rem" },
                  fontFamily: "var(--font-outfit)",
                  lineHeight: 1.4,
                }}
              >
                {t("onfolkTestimonial.stat1Desc")}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  color: "#9048FB",
                  fontWeight: 600,
                  fontSize: { xs: "1.25rem", md: "1.5rem" },
                  mb: 0.5,
                  fontFamily: "var(--font-plusjakarta)",
                }}
              >
                {t("onfolkTestimonial.stat2Label")}
              </Typography>
              <Typography
                sx={{
                  color: "#121212",
                  fontSize: { xs: "0.875rem", md: "1rem" },
                  fontFamily: "var(--font-outfit)",
                  lineHeight: 1.4,
                }}
              >
                {t("onfolkTestimonial.stat2Desc")}
              </Typography>
            </Box>
          </Box>

          {/* Author */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: "#121212",
                fontFamily: "var(--font-outfit)",
                mb: 0.5,
              }}
            >
              {t("onfolkTestimonial.authorName")}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#475569",
                fontFamily: "var(--font-outfit)",
                mb: 3,
                fontWeight: 400,
              }}
            >
              {t("onfolkTestimonial.authorRole")}
            </Typography>
          </Box>

          {/* CTA Button */}
          <Button
            component={Link}
            href="free-trial-of-ai-recruiting-tool"
            variant="contained"
            endIcon={
              <ArrowForwardIcon
                sx={{ transform: isArabic ? "scaleX(-1)" : "none" }}
              />
            }
            sx={{
              backgroundColor: "#000",
              color: "#fff",
              textTransform: "none",
              borderRadius: "50px",
              px: 3,
              py: 1.5,
              fontSize: { xs: "0.9rem", md: "1rem" },
              fontWeight: 500,
              fontFamily: "var(--font-outfit)",
              transition: "transform 0.2s ease-in-out",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#775CE5",
                color: "#fff",
                boxShadow: "none",
                transform: "scale(1.05) translateY(-2px)",
              },
              "& .MuiButton-endIcon": {
                marginLeft: isArabic ? "16px" : "8px",
                marginRight: isArabic ? "5px" : "-4px",
                marginTop: isArabic ? "4px" : "0px",
              },
            }}
          >
            {t("onfolkTestimonial.cta")}
          </Button>
        </Box>

        {/* RIGHT SIDE */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mt: { xs: "-8%", md: 0 },
            display: { xs: "none", sm: "none", md: "flex" },
          }}
        >
          <Box
            sx={{
              position: "relative",
              backgroundImage: `url('${IMAGES.TOOLKIT_BG}')`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              borderRadius: "20px",
              mt: { xs: 0, md: "-10%" },
              ml: isArabic ? 0 : { xs: 0, md: "3%" },
              mr: isArabic ? { xs: 0, md: "3%" } : 0,
              width: "100%",
              maxWidth: 600,
              height: { xs: 400, md: 550 },
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
            }}
          >
            <Image
              src={IMAGES.ONFOLK_TESTIMONIAL_IMG}
              alt="Marcus Chen testimonial about AI recruiter conducting interviews in 12 languages"
              width={400}
              height={200}
              style={{
                borderRadius: "16px",
                width: "80%",
                height: "auto",
                objectFit: "contain",
              }}
              priority
            />
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default OnfolkTestimonial;
